import { ReviewItem, ReviewResponse } from '../types';
import { OfflineResponse, OfflineReview } from './offlineDatabase';
import { mapOfflineResponseToReviewResponse } from './reviewOfflineTransforms';

type ResponseSyncStatus = 'pending' | 'synced' | 'failed';

export interface OfflineResponseMutationDatabase {
  getResponsesByReviewId(reviewId: number): Promise<OfflineResponse[]>;
  createResponse(
    data: Omit<OfflineResponse, 'id' | 'tempId' | 'syncStatus' | 'createdAt' | 'updatedAt'>
  ): Promise<OfflineResponse>;
  updateResponse(responseId: number, updates: Partial<OfflineResponse>): Promise<void>;
  getResponse(responseId: number): Promise<OfflineResponse | null>;
  updateReview(reviewId: number, updates: Partial<OfflineReview>): Promise<void>;
  getReview(reviewId: number): Promise<OfflineReview | null>;
  recordOfflineMutation?(entry: {
    entityType: 'response';
    entityId?: number | null;
    reviewId?: number | null;
    itemId?: number | null;
    mutationType: string;
    payload?: unknown;
    previousSnapshot?: unknown;
    status?: 'pending' | 'synced' | 'failed' | 'conflict';
  }): Promise<number | undefined>;
  createRecoverySnapshot?(reviewId: number, reason: string): Promise<void>;
}

export interface OfflineResponseMutationCallbacks {
  trackDraftUpdate(
    draftKey: string,
    reviewId: number,
    itemId: number,
    data: Partial<ReviewResponse>
  ): void;
  registerPendingResponseTempId(tempId: string): void;
  enqueueResponseSync(reviewId: number): void;
}

export class OfflineResponseMutationService {
  constructor(
    private readonly database: OfflineResponseMutationDatabase,
    private readonly callbacks: OfflineResponseMutationCallbacks
  ) {}

  async updateOfflineResponse(
    reviewId: number,
    itemId: number,
    data: Partial<ReviewResponse>,
    enableAutoSave: boolean = true,
    syncStatus: ResponseSyncStatus = 'pending',
    mutationTimestamp?: Date
  ): Promise<ReviewResponse> {
    try {
      const draftKey = `${reviewId}_${itemId}`;

      if (enableAutoSave) {
        this.callbacks.trackDraftUpdate(draftKey, reviewId, itemId, data);
      }

      const responses = await this.database.getResponsesByReviewId(reviewId);
      const matchingResponses = responses.filter(r => r.itemId === itemId);
      let existingResponse = matchingResponses[0];
      const previousSnapshot = existingResponse ? { ...existingResponse } : null;
      const now = mutationTimestamp || new Date();
      const isServerAcknowledgedUpdate = syncStatus === 'synced';
      const review = await this.database.getReview(reviewId).catch(() => null);
      const itemSnapshot = existingResponse?.itemSnapshot || this.findItemInReviewTemplate(review, itemId);

      if (!existingResponse) {
        console.error('[OfflineReviewService] Creating initial response for item', itemId, 'to enable photo queueing');

        const responseData: Omit<OfflineResponse, 'id' | 'tempId' | 'syncStatus' | 'createdAt' | 'updatedAt'> = {
          reviewId,
          itemId,
          roomId: data.roomId,
          floor: data.floor,
          isPresent: data.isPresent ?? true,
          status: data.status ?? 'not_started',
          responseData: data.responseData || {},
          notes: data.notes || '',
          ...(data.findings !== undefined ? { findings: data.findings } : {}),
          ...(itemSnapshot ? { itemSnapshot } : {}),
        };

        existingResponse = await this.database.createResponse(responseData);

        if (syncStatus === 'pending' && existingResponse.tempId) {
          this.callbacks.registerPendingResponseTempId(existingResponse.tempId);
        }

        if (syncStatus === 'pending') {
          await this.recordResponseMutation(reviewId, itemId, existingResponse.id, data, previousSnapshot, 'response_create');
          await this.database.createRecoverySnapshot?.(reviewId, 'response_create');
          await this.markReviewPendingForResponseSync(reviewId, now);
        } else if (isServerAcknowledgedUpdate) {
          await this.database.updateResponse(existingResponse.id, {
            syncStatus: 'synced',
            syncError: null,
            lastServerSeenAt: now,
            serverRevision: (existingResponse.serverRevision ?? 0) + 1,
            conflictStatus: 'none',
          });
        }
      }

      if (existingResponse) {
        const updates: Partial<OfflineResponse> = {
          isPresent: data.isPresent,
          status: data.status,
          responseData: data.responseData,
          notes: data.notes,
          findings: data.findings,
          roomId: data.roomId,
          floor: data.floor,
          itemSnapshot: itemSnapshot || undefined,
          syncStatus,
          ...(isServerAcknowledgedUpdate
            ? {
                syncError: null,
                lastServerSeenAt: now,
                serverRevision: (existingResponse.serverRevision ?? 0) + 1,
                conflictStatus: 'none' as const,
              }
            : {
                lastLocalMutationAt: now,
                localRevision: (existingResponse.localRevision ?? 0) + 1,
                conflictStatus: 'none' as const,
              }),
        };

        const updatedResponse = await this.updateResponseWithRepair(
          reviewId,
          itemId,
          existingResponse,
          updates,
          data,
          {
            reviewId,
            itemId,
            roomId: data.roomId,
            floor: data.floor,
            isPresent: data.isPresent ?? false,
            status: data.status ?? 'not_started',
            responseData: data.responseData || {},
            notes: data.notes || '',
            findings: data.findings || [],
            itemSnapshot: itemSnapshot || undefined,
          },
          syncStatus
        );

        const duplicateResponses = matchingResponses.filter(r => r.id !== existingResponse!.id);
        for (const duplicateResponse of duplicateResponses) {
          await this.database.updateResponse(duplicateResponse.id, {
            ...updates,
            localRevision: isServerAcknowledgedUpdate
              ? duplicateResponse.localRevision
              : (duplicateResponse.localRevision ?? 0) + 1,
            serverRevision: isServerAcknowledgedUpdate
              ? (duplicateResponse.serverRevision ?? 0) + 1
              : duplicateResponse.serverRevision,
          });
        }

        if (syncStatus === 'pending') {
          await this.recordResponseMutation(reviewId, itemId, updatedResponse.id, data, previousSnapshot, 'response_update');
          await this.database.createRecoverySnapshot?.(reviewId, 'response_update');
          await this.markReviewPendingForResponseSync(reviewId, now);
        }

        await this.advanceDraftReviewWhenResponseStarted(reviewId, data.status);

        return mapOfflineResponseToReviewResponse(updatedResponse);
      }

      const responseData: Omit<OfflineResponse, 'id' | 'tempId' | 'syncStatus' | 'createdAt' | 'updatedAt'> = {
        reviewId,
        itemId,
        roomId: data.roomId,
        floor: data.floor,
        isPresent: data.isPresent ?? false,
        status: data.status ?? 'not_started',
        responseData: data.responseData,
        notes: data.notes,
        findings: data.findings || [],
      };

      const newOfflineResponse = await this.database.createResponse(responseData);
      await this.recordResponseMutation(reviewId, itemId, newOfflineResponse.id, data, null, 'response_create');
      await this.database.createRecoverySnapshot?.(reviewId, 'response_create');
      await this.markReviewPendingForResponseSync(reviewId, now);

      if (newOfflineResponse.tempId) {
        this.callbacks.registerPendingResponseTempId(newOfflineResponse.tempId);
      }

      await this.advanceDraftReviewWhenResponseStarted(reviewId, data.status);

      return mapOfflineResponseToReviewResponse(newOfflineResponse);
    } catch (error) {
      console.warn('[OfflineReviewService] Error updating offline response:', error);
      throw error;
    }
  }

  private async updateResponseWithRepair(
    reviewId: number,
    itemId: number,
    existingResponse: OfflineResponse,
    updates: Partial<OfflineResponse>,
    expectedData: Partial<ReviewResponse>,
    createData: Omit<OfflineResponse, 'id' | 'tempId' | 'syncStatus' | 'createdAt' | 'updatedAt'>,
    syncStatus: ResponseSyncStatus
  ): Promise<OfflineResponse> {
    let lastError: unknown;
    const attemptedIds = new Set<number>();

    const tryUpdate = async (candidate: OfflineResponse): Promise<OfflineResponse | null> => {
      if (!candidate.id || attemptedIds.has(candidate.id)) {
        return null;
      }

      attemptedIds.add(candidate.id);

      try {
        await this.database.updateResponse(candidate.id, updates);
        const updated = await this.database.getResponse(candidate.id);
        if (updated && this.responseMatchesExpectedData(updated, expectedData)) {
          return updated;
        }
      } catch (error) {
        lastError = error;
      }

      return null;
    };

    const selectedUpdate = await tryUpdate(existingResponse);
    if (selectedUpdate) {
      return selectedUpdate;
    }

    const refreshedResponses = await this.database.getResponsesByReviewId(reviewId).catch(error => {
      lastError = error;
      return [];
    });
    const matchingResponses = refreshedResponses.filter(response => response.itemId === itemId);

    for (const candidate of matchingResponses) {
      const repaired = await tryUpdate(candidate);
      if (repaired) {
        return repaired;
      }
    }

    const createdResponse = await this.database.createResponse(createData);
    if (syncStatus === 'pending' && createdResponse.tempId) {
      this.callbacks.registerPendingResponseTempId(createdResponse.tempId);
    }

    const createdUpdate = await tryUpdate(createdResponse);
    if (createdUpdate) {
      return createdUpdate;
    }

    throw new Error(
      `Failed to persist response ${reviewId}/${itemId}${lastError instanceof Error ? `: ${lastError.message}` : ''}`
    );
  }

  private responseMatchesExpectedData(
    response: OfflineResponse,
    expectedData: Partial<ReviewResponse>
  ): boolean {
    if (expectedData.isPresent !== undefined && response.isPresent !== expectedData.isPresent) {
      return false;
    }

    if (expectedData.status !== undefined && response.status !== expectedData.status) {
      return false;
    }

    if (expectedData.notes !== undefined && (response.notes || '') !== (expectedData.notes || '')) {
      return false;
    }

    if (
      expectedData.findings !== undefined &&
      JSON.stringify(response.findings || []) !== JSON.stringify(expectedData.findings || [])
    ) {
      return false;
    }

    if (
      expectedData.responseData !== undefined &&
      JSON.stringify(response.responseData || {}) !== JSON.stringify(expectedData.responseData || {})
    ) {
      return false;
    }

    if (expectedData.roomId !== undefined && response.roomId !== expectedData.roomId) {
      return false;
    }

    if (expectedData.floor !== undefined && response.floor !== expectedData.floor) {
      return false;
    }

    return true;
  }

  private async markReviewPendingForResponseSync(reviewId: number, mutationTimestamp: Date): Promise<void> {
    const currentReview = await this.database.getReview(reviewId);
    await this.database.updateReview(reviewId, {
      syncStatus: 'pending',
      lastLocalMutationAt: mutationTimestamp,
      localRevision: (currentReview?.localRevision ?? 0) + 1,
      conflictStatus: 'none',
    });

    this.callbacks.enqueueResponseSync(reviewId);
  }

  private findItemInReviewTemplate(
    review: OfflineReview | null,
    itemId: number
  ): ReviewItem | undefined {
    const search = (categories: any[] | undefined): ReviewItem | undefined => {
      for (const category of categories || []) {
        const items = Array.isArray(category?.items)
          ? category.items
          : Array.isArray(category?.reviewItems)
            ? category.reviewItems
            : [];
        const item = items.find((candidate: ReviewItem | any) =>
          Number(candidate?.id ?? candidate?.itemId) === Number(itemId)
        );
        if (item) {
          return item;
        }

        const nested = search(category?.subcategories || category?.children);
        if (nested) {
          return nested;
        }
      }

      return undefined;
    };

    return search((review as any)?.template?.categories);
  }

  private async recordResponseMutation(
    reviewId: number,
    itemId: number,
    entityId: number | undefined,
    payload: Partial<ReviewResponse>,
    previousSnapshot: OfflineResponse | null,
    mutationType: string
  ): Promise<void> {
    await this.database.recordOfflineMutation?.({
      entityType: 'response',
      entityId: entityId ?? null,
      reviewId,
      itemId,
      mutationType,
      payload,
      previousSnapshot,
      status: 'pending',
    });
  }

  private async advanceDraftReviewWhenResponseStarted(
    reviewId: number,
    status: ReviewResponse['status'] | undefined
  ): Promise<void> {
    if (!status || status === 'not_started') {
      return;
    }

    const currentReview = await this.database.getReview(reviewId);
    if (currentReview?.status === 'draft') {
      await this.database.updateReview(reviewId, {
        status: 'in_progress',
      });
    }
  }
}
